'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { AvisoTrialGate } from '@/features/contracts/components/AvisoTrialGate';
import { GuestWorkspaceBanner } from '@/features/layout/components/GuestWorkspaceBanner';
import { MobileNavbar } from '@/features/layout/components/MobileNavbar';
import { MobileSidebarDrawer } from '@/features/layout/components/MobileSidebarDrawer';
import { Sidebar } from '@/features/layout/components/Sidebar';
import LoadingBar from '@/shared/components/ui/common/LoadingBar';
import { useLayout } from '@/shared/hooks/useLayout';
import { Usuario } from '@/types';
interface LayoutProps {
  children: React.ReactNode;
  user?: Usuario;
  notificationCount?: number;
  onNotificationClick?: () => void;
  onProfileClick?: () => void;
  onLogout?: () => void;
  isLoading?: boolean;
  className?: string;
}
const sidebarCollapsedStorageKey = 'sidebar-collapsed';
const safeGetLocalStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSetLocalStorageItem = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    return;
  }
};
const isNegociacaoDetailPath = (path: string) => /^\/negociacoes\/[^/]+\/?$/.test(path) && !['nova', 'criar', 'lote', 'lotes', 'ajustes', 'simulacao'].includes(path.split('/')[2]);
const isNotificacaoDetailPath = (path: string) => /^\/notificacoes\/[^/]+\/?$/.test(path) && !['nova', 'criar', 'lote', 'lotes', 'ajustes', 'recebidas', 'certificado'].includes(path.split('/')[2]);
const isFocusedDetailPath = (path: string) => isNegociacaoDetailPath(path) || isNotificacaoDetailPath(path);
const shouldAutoCollapse = (path: string) => {
  const autoCollapseRoutes = ['/notificacoes/criar', '/notificacoes/nova', '/negociacoes/nova', '/negociacoes/criar', '/peticionamento/criar', '/peticionamento/novo', '/mediacoes/nova', '/mediacoes/criar', '/contratos/novo', '/arbitral/novo'];
  const isContratoDetailOrEdit = path.startsWith('/contratos/') && path !== '/contratos' && !path.includes('/contratos?');
  const isChat = path === '/chat' || path.startsWith('/chat/');
  return autoCollapseRoutes.some(route => path.includes(route)) || isContratoDetailOrEdit || isChat || isFocusedDetailPath(path);
};
const shouldHideSidebar = (path: string) => {
  return path.startsWith('/notificacoes/recebidas') || path.startsWith('/admin-geral/flows/editor');
};
const Layout: React.FC<LayoutProps> = t0 => {
  const $ = _c(44);
  const {
    children,
    isLoading: t1,
    className: t2
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const className = t2 === undefined ? "" : t2;
  const {
    currentRoute
  } = useLayout();
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [userPreference, setUserPreference] = useState(null);
  let t3;
  if ($[0] !== pathname) {
    t3 = pathname ? shouldHideSidebar(pathname) : false;
    $[0] = pathname;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const isSidebarHidden = t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const saved = safeGetLocalStorageItem(sidebarCollapsedStorageKey);
      if (saved !== null) {
        const savedValue = saved === "true";
        setUserPreference(savedValue);
        setIsSidebarCollapsed(savedValue);
      }
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[4] !== pathname || $[5] !== userPreference) {
    t6 = () => {
      if (pathname) {
        const shouldCollapse = shouldAutoCollapse(pathname);
        if (shouldCollapse) {
          setIsSidebarCollapsed(true);
        } else {
          if (userPreference !== null) {
            setIsSidebarCollapsed(userPreference);
          } else {
            setIsSidebarCollapsed(false);
          }
        }
      }
    };
    t7 = [pathname, userPreference];
    $[4] = pathname;
    $[5] = userPreference;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] !== isMobileMenuOpen) {
    t8 = () => {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    };
    $[8] = isMobileMenuOpen;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleMobileMenuToggle = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsMobileMenuOpen(false);
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleMobileMenuClose = t9;
  let t10;
  if ($[11] !== isSidebarCollapsed || $[12] !== pathname) {
    t10 = () => {
      const newValue = !isSidebarCollapsed;
      setIsSidebarCollapsed(newValue);
      if (!pathname || !isFocusedDetailPath(pathname)) {
        setUserPreference(newValue);
        safeSetLocalStorageItem(sidebarCollapsedStorageKey, String(newValue));
      }
    };
    $[11] = isSidebarCollapsed;
    $[12] = pathname;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const handleToggleSidebar = t10;
  let t11;
  if ($[14] !== isLoading) {
    t11 = <LoadingBar isLoading={isLoading} />;
    $[14] = isLoading;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== handleMobileMenuToggle || $[17] !== isMobileMenuOpen || $[18] !== isSidebarHidden) {
    t12 = !isSidebarHidden && <><MobileNavbar onMenuClick={handleMobileMenuToggle} /><MobileSidebarDrawer open={isMobileMenuOpen} onClose={handleMobileMenuClose}><div className="h-full w-full"><Sidebar isMobile={true} onNavigate={handleMobileMenuClose} /></div></MobileSidebarDrawer></>;
    $[16] = handleMobileMenuToggle;
    $[17] = isMobileMenuOpen;
    $[18] = isSidebarHidden;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = `flex h-screen overflow-hidden bg-white ${className}`;
  let t14;
  if ($[20] !== handleToggleSidebar || $[21] !== isSidebarCollapsed || $[22] !== isSidebarHidden) {
    t14 = !isSidebarHidden && <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={handleToggleSidebar} />;
    $[20] = handleToggleSidebar;
    $[21] = isSidebarCollapsed;
    $[22] = isSidebarHidden;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <GuestWorkspaceBanner />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = `min-h-0 flex-1 overflow-auto ${isSidebarHidden ? "pt-0" : "pt-16"} lg:pt-0`;
  let t17;
  if ($[25] !== currentRoute.title) {
    t17 = <h1 className="sr-only">{currentRoute.title}</h1>;
    $[25] = currentRoute.title;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== children || $[28] !== t17) {
    t18 = <div className="flex h-full min-h-full flex-col bg-white">{t17}{children}</div>;
    $[27] = children;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t16 || $[31] !== t18) {
    t19 = <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col rounded-tl-2xl border-b border-neutral-200 transition-all duration-300 md:rounded-tl-2xl md:border-t md:border-neutral-200"><div className="flex h-full min-h-0 flex-col overflow-hidden md:border-t md:border-neutral-200">{t15}<main className={t16} role="main">{t18}</main></div></div>;
    $[30] = t16;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t19) {
    t20 = <div className={t13}>{t14}{t19}</div>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== isSidebarHidden) {
    t21 = !isSidebarHidden && <AvisoTrialGate />;
    $[37] = isSidebarHidden;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t11 || $[40] !== t12 || $[41] !== t20 || $[42] !== t21) {
    t22 = <>{t11}{t12}{t20}{t21}</>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  return t22;
};
export default Layout;